/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useFeatureCodeModifications.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    Custom hook for managing Feature Code modifications and batch updates.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { updateFeatureCode } from '../api/featureCodesApi';
import type { FeatureCode, FeatureCodeModification } from '../types';

interface UseFeatureCodeModificationsOptions {
  featureCodes: FeatureCode[];
  originalValues: Map<string, { default_number: string; custom_number: string | null }>;
  tenantSlug?: string;
  userId?: string;
  onSaveSuccess?: () => void;
}

export function useFeatureCodeModifications({
  featureCodes,
  originalValues,
  tenantSlug,
  userId,
  onSaveSuccess,
}: UseFeatureCodeModificationsOptions) {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [modifiedCodes, setModifiedCodes] = useState<Map<string, FeatureCodeModification>>(new Map());
  const [saving, setSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return modifiedCodes.size > 0;
  }, [modifiedCodes]);

  // Get effective value for a field
  const getEffectiveValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (featureCode: FeatureCode, field: keyof FeatureCode): any => {
      const identifier = featureCode.uuid || featureCode.id.toString();
      const modified = modifiedCodes.get(identifier);
      return modified && field in modified ? modified[field] : featureCode[field];
    },
    [modifiedCodes]
  );

  // Check if a feature code has been modified
  const isModified = useCallback(
    (featureCodeId: string): boolean => {
      const featureCode = featureCodes.find((fc) => (fc.uuid || fc.id.toString()) === featureCodeId);
      if (!featureCode) return false;

      const effectiveCustomNumber = getEffectiveValue(featureCode, 'custom_number');
      return (
        effectiveCustomNumber !== null &&
        effectiveCustomNumber !== '' &&
        effectiveCustomNumber !== featureCode.default_number
      );
    },
    [featureCodes, getEffectiveValue]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (featureCodeId: string, field: keyof FeatureCode, value: any) => {
      setModifiedCodes((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(featureCodeId) || {};
        newMap.set(featureCodeId, { ...existing, [field]: value });
        return newMap;
      });
    },
    []
  );

  // Reset feature code to original values
  const handleReset = useCallback(
    (featureCodeId: string) => {
      setModifiedCodes((prev) => {
        const newMap = new Map(prev);
        newMap.set(featureCodeId, { custom_number: null });
        return newMap;
      });
    },
    []
  );

  // Handle save (batch update)
  const handleSave = useCallback(async () => {

    try {
      setSaving(true);
      let successCount = 0;
      let errorCount = 0;

      for (const [featureCodeId, changes] of modifiedCodes.entries()) {
        try {
          await updateFeatureCode(featureCodeId, changes, tenantSlug, userId);
          successCount++;
        } catch (error) {
          console.error(`Error updating feature code ${featureCodeId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSuccess(t('featureCodes.updateSuccess', { count: successCount }));
        setModifiedCodes(new Map());
        onSaveSuccess?.();
      }

      if (errorCount > 0) {
        showError(t('featureCodes.updateError', { count: errorCount }));
      }
    } catch (error) {
      console.error('Error saving feature codes:', error);
      showError(t('featureCodes.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [hasChanges, modifiedCodes, tenantSlug, userId, showSuccess, showError, showInfo, t, onSaveSuccess]);

  return {
    modifiedCodes,
    saving,
    hasChanges,
    getEffectiveValue,
    isModified,
    handleFieldChange,
    handleReset,
    handleSave,
  };
}

